/**
 * What every scored screen shares: who sees scores, the badge, and a rule's
 * conditions in words.
 *
 * Scores are internal. A buyer, supplier or pending login, or anyone limited
 * to some campaigns, buyers or suppliers, never sees one (the server sends
 * none and refuses every scoring endpoint), so nothing here renders for them.
 *
 * Scoring is not part of the CRM area alone: routing can send hot leads to a
 * premium tier, so lead scores and their rules stay with distribution on.
 * Person rules and decay are about people, so they follow the CRM area.
 */
import { Badge, Tooltip, type BadgeProps } from '@mantine/core';
import { canDo } from '../../layout/commands';
import { isScopedUser } from '../../layout/nav';
import { useNavUser } from '../../layout/navUser';
import { useScoringRules, type ScoreBandRef } from '../../api/scoring';

export interface ScoringPermissions {
  /** Scores on rows, pages and filters. */
  sees: boolean;
  /** Settings › Scoring (manage_scoring). */
  manage: boolean;
  /** Person rules, decay and person scores (the CRM area). */
  people: boolean;
}

export function scoringPermissions(user: ReturnType<typeof useNavUser>): ScoringPermissions {
  const sees = !!user && !isScopedUser(user);
  return {
    sees,
    manage: sees && canDo(user, 'manage_scoring'),
    people: sees,
  };
}

export function useScoringPermissions(): ScoringPermissions {
  return scoringPermissions(useNavUser());
}

/**
 * Whether this organisation scores at all (any rule, on or off), for a
 * member who sees scores. Score columns and filters wait for it, so nothing
 * about scores shows until someone sets scoring up.
 */
export function useScoringInUse(): boolean {
  const p = useScoringPermissions();
  const rules = useScoringRules(p.sees);
  return p.sees && (rules.data?.rules?.length ?? 0) > 0;
}

/** The band a score falls in, from the organisation's bands (lowest first). */
export function bandFor(
  bands: { key: string; label: string; min?: number | null; colour: string }[] | undefined,
  score: number | null | undefined,
): ScoreBandRef | null {
  if (score == null || !bands?.length) return null;
  let found = bands[0];
  for (const b of bands) if (b.min == null || score >= b.min) found = b;
  return found ? { key: found.key, label: found.label, colour: found.colour } : null;
}

/** A Mantine colour for a band, from the palette names bands are stored with. */
export function bandColour(band: ScoreBandRef | null | undefined): string {
  return band?.colour || 'gray';
}

/** "+25" or "-10". */
export function signed(points: number): string {
  return points > 0 ? `+${points}` : `${points}`;
}

/**
 * A score with its band's colour: "45" on a warm orange. The band's name is
 * in the tooltip and the accessible name, so colour is never the only cue.
 */
export function ScoreBadge({
  score,
  band,
  size = 'sm',
  withLabel = false,
  ...rest
}: {
  score: number | null | undefined;
  band?: ScoreBandRef | null;
  withLabel?: boolean;
} & Omit<BadgeProps, 'children' | 'color'>) {
  if (score == null) return null;
  const text = withLabel && band ? `${score} · ${band.label}` : `${score}`;
  const name = band ? `Score ${score}, ${band.label}` : `Score ${score}`;
  return (
    <Tooltip label={name} withArrow openDelay={300}>
      <Badge
        size={size}
        variant="light"
        color={bandColour(band)}
        radius="sm"
        tt="none"
        aria-label={name}
        data-testid="score-badge"
        style={{ fontVariantNumeric: 'tabular-nums' }}
        {...rest}
      >
        {text}
      </Badge>
    </Tooltip>
  );
}

const OPERATOR_WORDS: Record<string, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  greater_than: 'is more than',
  less_than: 'is less than',
  greater_equal: 'is at least',
  less_equal: 'is at most',
  in: 'is one of',
  not_in: 'is none of',
  regex: 'matches',
  is_empty: 'is empty',
  is_not_empty: 'is filled in',
};

/** A rule's conditions as one short line: "residential_status is homeowner and ...". */
export function describeConditions(raw: unknown, labelOf: (field: string) => string = (f) => f): string {
  if (!Array.isArray(raw)) return 'No conditions';
  type Cond = { type?: string; enabled?: boolean; logic?: string; config?: Record<string, string> };
  const on = (raw as Cond[]).filter((c) => c.enabled !== false);
  const words = (c: Cond) => {
      const cfg = c.config ?? {};
      switch (c.type ?? 'field') {
        case 'field': {
          const op = OPERATOR_WORDS[cfg.operator ?? 'equals'] ?? cfg.operator ?? '';
          const value = ['is_empty', 'is_not_empty'].includes(cfg.operator ?? '') ? '' : ` ${cfg.value ?? ''}`;
          return `${labelOf(cfg.field ?? '')} ${op}${value}`.trim();
        }
        case 'campaign_match':
          return `${labelOf(cfg.field ?? '')} ${cfg.condition === 'not_in' ? 'is not on' : 'is on'} a list`;
        case 'geographic':
          return `${cfg.location_type ?? 'location'} in ${cfg.value ?? ''}`.trim();
        case 'time_based':
          return cfg.day_type === 'weekend' ? 'arrives at a weekend' : cfg.day_type === 'weekday' ? 'arrives on a weekday' : 'any day';
        default:
          return c.type ?? '';
      }
  };
  const all = on.filter((c) => (c.logic ?? 'and') !== 'or').map(words);
  const any = on.filter((c) => c.logic === 'or').map(words);
  if (all.length === 0 && any.length === 0) return 'No conditions';
  const either = any.length > 1 ? `(${any.join(' or ')})` : any[0];
  return [...all, ...(either ? [either] : [])].join(' and ');
}
